import { BasePresenter } from "core/presenter";
import { CustomerRepository, NewSaleReportRepository } from "core/repository";

import Moment from "moment";

import * as FileSaver from "file-saver";
import { fn } from "util";
import { DocumentTypes } from "core/enum";

class CustomerSalesReportPresenter extends BasePresenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      loading: true,
      filters: {
        dateFrom: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        dateTo: endDate.isValid() ? endDate : Moment().endOf("day")
      },
      pagination: {
        page: 1,
        perPage: 25,
        total: 0
      },
      order: {
        field: "name",
        direction: "ASC"
      },
      results: [],
      summary: {
        total_revenues: 0,
        total_costs: 0,
        profit: 0
      },
      customerSearchOptions: {
        online: true,
        repository: CustomerRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: async filter => await this.getCustomerFilters(filter)
      },
      customer: {
        name: "",
        uuid: null
      },
      mode: "customer",
      customerSelectedDataTable: {
        name: "",
        uuid: null
      }
    };
  }

  async onStart() {
    this.load();
  }

  msgError(content) {
    this.sendNotification({
      type: "error",
      title: "Oops",
      content: content
    });
  }

  makePagination(perPage, total) {
    this.viewModel.pagination = {
      ...this.viewModel.pagination,
      total: total,
      perPage: perPage
    };
  }

  async load() {
    this.viewModel.loading = true;
    this.viewModel.results = [];

    let resultsPromise;

    if (this.viewModel.customer.uuid) {
      this.viewModel.mode = "sale";
      resultsPromise = this.loadSaleResults();
    } else {
      this.viewModel.mode = "customer";
      resultsPromise = this.loadCustomerResults();
    }

    await Promise.all([resultsPromise, this.loadSummary(this.viewModel.mode)]);

    this.viewModel.loading = false;
  }

  cleanResults() {
    this.viewModel.results = [];
  }

  async loadCustomerResults() {
    this.renewCancelToken();
    this.cleanResults();
    const filters = await this.getReportFilters();

    const repositoryResult = await NewSaleReportRepository.getReportCustomerResults(
      filters
    );

    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.customers;
      const perPage = repositoryResult.data.pagination.perPage;
      const total = repositoryResult.data.pagination.total;
      this.makePagination(perPage, total);
    } else if (!repositoryResult.userCancelled) {
      this.msgError(
        "Não foi possível carregar os clientes.\nTente novamente mais tarde ou entre em contato com o suporte."
      );
    }
  }

  async loadSaleResults() {
    this.renewCancelToken();
    this.cleanResults();

    const filters = await this.getReportFilters();

    const repositoryResult = await NewSaleReportRepository.getReportCustomerSalesResults(
      filters
    );

    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.sales;

      const perPage = repositoryResult.data.pagination.perPage;
      const total = repositoryResult.data.pagination.total;
      this.makePagination(perPage, total);
    } else if (!repositoryResult.userCancelled) {
      this.msgError(
        "Não foi possível carregar as vendas do vendedor selecionado.\nTente novamente mais tarde ou entre em contato com o suporte."
      );
    }
  }

  cleanSummary() {
    this.viewModel.summary.total_revenues = 0;
    this.viewModel.summary.total_costs = 0;
    this.viewModel.summary.profit = 0;
  }

  async loadSummary(product) {
    this.cleanSummary();
    const filters = await this.getReportFilters();

    if (product === "sale") {
      const repositoryResult = await NewSaleReportRepository.getReportCustomerResults(
        filters
      );
      if (repositoryResult.success && repositoryResult.data.customers.length) {
        this.viewModel.summary.total_revenues =
          repositoryResult.data.customers[0].revenues_value;
        this.viewModel.summary.total_costs =
          repositoryResult.data.customers[0].costs_value;
        this.viewModel.summary.profit =
          repositoryResult.data.customers[0].profit;
      }
    } else {
      const repositoryResult = await NewSaleReportRepository.getReportCustomerSummary(
        filters
      );

      if (repositoryResult.success) {
        this.viewModel.summary = repositoryResult.data;
      }
    }
  }

  async getReportFilters() {
    const { customer, filters, pagination } = this.viewModel;
    const { dateFrom, dateTo } = filters;
    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;

    const order = this.getOrder();
    const reportFilters = {
      company_uuid: company_uuid,
      from: Moment(dateFrom)
        .startOf("day")
        .toISOString(),
      to: Moment(dateTo)
        .endOf("day")
        .toISOString(),
      page: pagination.page,
      customer: customer.uuid,
      order: order.direction === "DESC" ? `-${order.field}` : order.field
    };

    return reportFilters;
  }

  async getCustomerFilters(query) {
    const companyUuid = (await this.mRuntime.get("currentUser")).company_uuid;
    const filters = [];

    if (query) {
      ["name", "identification", "email"].forEach(field =>
        filters.push({
          connector: "OR",
          field,
          operation: "LIKE",
          value: query
        })
      );
    }

    filters.push({
      connector: "AND",
      field: "company_uuid",
      operation: "EQ",
      value: companyUuid
    });

    return filters;
  }

  async onDownload(format) {
    this.viewModel.loading = true;
    if (format === "csv") {
      await this._downloadCSV();
    } else {
      this.msgError("Ainda não suportamos esse formato");
    }
    this.viewModel.loading = false;
  }

  formatDocumentType(type) {
    return DocumentTypes[type];
  }

  async loadSaleResultsDownload() {
    this.renewCancelToken();
    const filters = await this.getReportFilters();
    filters.count = 99999;
    const repositoryResult = await NewSaleReportRepository.getReportCustomerSalesResults(
      filters
    );
    if (repositoryResult.success) {
      return repositoryResult.data.sales;
    }
  }

  async loadCustomerResultsDownload() {
    this.renewCancelToken();
    const filters = await this.getReportFilters();
    filters.count = 99999;
    const repositoryResult = await NewSaleReportRepository.getReportCustomerResults(
      filters
    );
    if (repositoryResult.success) {
      return repositoryResult.data.customers;
    }
  }

  async _downloadCSV() {
    let results = [];
    let content = [];
    if (this.viewModel.mode === "customer") {
      results = await this.loadCustomerResultsDownload();
      content = [
        "Cliente; Quantidade de Compras; Faturamento; Custos; Lucro Bruto"
      ];
      results.forEach(item => {
        const revenuesValue = fn.centsToReal(item.revenues_value);
        const costValue = fn.centsToReal(item.costs_value);
        const profitValue = fn.centsToReal(item.profit);
        content.push(
          `${item.customer_name || "Nao informado"};  ${item.sales_count ||
            1};${revenuesValue};${costValue};${profitValue}`
        );
      });
      const contentBlob = new Blob([content.join("\n")], {
        type: "application/csv;charset=utf-8"
      });
      FileSaver.saveAs(contentBlob, "relatorio-de-vendas.csv");
    } else {
      results = await this.loadSaleResultsDownload();
      content = ["Data; Documento; Status; Faturamento; Custos; Lucro"];
      results.forEach(item => {
        const revenuesValue = fn.centsToReal(item.revenues_value);
        const costValue = fn.centsToReal(item.costs_value);
        const profitValue = fn.centsToReal(item.profit);
        const date = Moment(item.activated_at).format("DD/MM/YYYY HH:mm");
        content.push(
          `${date};${this.formatDocumentType(item.type)} n.: ${item.number}; ${
            item.status
          };${revenuesValue};${costValue};${profitValue}`
        );
      });
      const contentBlob = new Blob([content.join("\n")], {
        type: "application/csv;charset=utf-8"
      });
      FileSaver.saveAs(
        contentBlob,
        `relatorio-de-vendas-${this.viewModel.customer.name}.csv`
      );
    }
  }

  getOrder() {
    const { order } = this.viewModel;

    if (order.field === "name") {
      return [
        { direction: order.direction, field: "customer.name" },
        { direction: order.direction, field: "customer.identification" },
        { direction: order.direction, field: "customer.email" }
      ];
    }

    if (this.viewModel.mode === "sale" && order.field === "document_number") {
      return { direction: order.direction, field: "sale.document.number" };
    }

    return order;
  }

  onSelectCustomerButton(customerSelected, uuid, name) {
    this.viewModel.customerSelectedDataTable = {
      name: name,
      uuid: uuid
    };
    if (customerSelected.value === "") {
      this.viewModel.customer = customerSelected;
      this.viewModel.customer.uuid = uuid;
      this.viewModel.mode = "sale";
    }
    this.viewModel.customer = customerSelected;
    this.viewModel.customer.uuid = uuid;
    this.viewModel.pagination.page = 1;

    if (uuid) {
      this.viewModel.order = { field: "sale.activated_at", direction: "ASC" };
    }
    this.load();
  }

  onSelectCustomer(selectedCustomer) {
    this.viewModel.customerSelectedDataTable = {
      name: "",
      uuid: null
    };
    if (selectedCustomer.name === "") {
      this.viewModel.customer = selectedCustomer;
      this.viewModel.mode = "sale";
      this.load();
    }
    this.viewModel.customer = selectedCustomer;
    this.viewModel.pagination.page = 1;

    if (selectedCustomer.uuid) {
      this.viewModel.order = { field: "sale.activated_at", direction: "ASC" };
    }

    this.load();
  }

  onFilterChange(filters) {
    if (filters.dateFrom && filters.dateTo) {
      window.location.hash = `?&inicio=${Moment(filters.dateFrom).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filters.dateTo).format("DD-MM-YYYY")}`;
    }
    this.viewModel.filters = { ...this.viewModel.filters, ...filters };
    this.viewModel.pagination.page = 1;
    this.load();
  }

  onPageChange(page) {
    this.viewModel.pagination.page = page;
    this.load();
  }

  onOrderChange(field, direction) {
    this.viewModel.order = { field, direction };
    this.load();
  }
}

export default CustomerSalesReportPresenter;



// WEBPACK FOOTER //
// ./src/components/app/management/customer-sales-report/presenter.js